import React, { useCallback, useEffect, useState } from "react";
import { Button, Upload, Tag, Tooltip, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages, faRotate } from "@fortawesome/free-solid-svg-icons";
import landingPageApi from "../../../api/landingPageApi";
import { extractPageImages, probePageImages, toPageImageName } from "./landing-page-images";

/**
 * Bảng "ảnh trang này cần" — liệt kê mọi ảnh /img/pages/ mà HTML đang trỏ tới và
 * cho biết file đã nằm trên server hay chưa, kèm nút tải NHIỀU ảnh một lượt.
 *
 * Có bảng này vì deploy KHÔNG đẩy thư mục src/public/img: HTML lên trang ngon lành
 * nhưng ảnh thiếu thì trang vỡ mà không ai biết cho tới lúc user mở.
 *
 * Backend giữ nguyên tên file admin upload, nên chỉ cần chọn đúng file là tự khớp
 * với <img src> có sẵn — không phải copy URL dán tay.
 */
function LandingPageImages({ html, apiBase }) {
    const [images, setImages] = useState([]);
    const [status, setStatus] = useState({});
    const [checking, setChecking] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Ảnh trỏ tương đối (/img/pages/x.webp) phải ghép host API, không thì trình duyệt
    // đi tìm trên chính origin của admin và luôn báo thiếu.
    const absolute = useCallback((url) => (url.startsWith("/") ? `${apiBase}${url}` : url), [apiBase]);

    const check = useCallback(
        async (list) => {
            if (!list.length) return setStatus({});
            setChecking(true);
            try {
                setStatus(await probePageImages(list.map((i) => ({ ...i, url: absolute(i.url) }))));
            } finally {
                setChecking(false);
            }
        },
        [absolute]
    );

    // Gõ tới đâu quét tới đó sẽ nã request liên tục → chờ ngơi tay 600ms mới kiểm.
    useEffect(() => {
        const timer = setTimeout(() => {
            const list = extractPageImages(html);
            setImages(list);
            check(list);
        }, 600);
        return () => clearTimeout(timer);
    }, [html, check]);

    const uploadAll = async (files) => {
        setUploading(true);
        let ok = 0;
        for (const file of files) {
            const formData = new FormData();
            formData.append("image", file);
            try {
                await landingPageApi.uploadImage(formData);
                ok += 1;
            } catch (error) {
                message.error(`${file.name}: ${error?.response?.data?.message || "tải lên thất bại"}`);
            }
        }
        setUploading(false);
        if (ok) message.success(`Đã tải ${ok}/${files.length} ảnh`);
        check(extractPageImages(html));
    };

    const missing = images.filter((i) => status[i.name] === false);

    return (
        <div className="border border-line rounded p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <label className="text-sm font-semibold">
                    Ảnh trang này cần{" "}
                    {images.length > 0 && (
                        <span className="font-normal text-content-3">
                            ({images.length - missing.length}/{images.length} đã có)
                        </span>
                    )}
                </label>
                <div className="flex items-center gap-2">
                    <Tooltip title="Kiểm tra lại">
                        <Button size="small" loading={checking} onClick={() => check(images)}>
                            <FontAwesomeIcon icon={faRotate} />
                        </Button>
                    </Tooltip>
                    <Upload
                        multiple
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file, fileList) => {
                            // beforeUpload chạy 1 lần/file; chỉ nhận ở file đầu để upload trọn lô 1 lượt.
                            if (file === fileList[0]) uploadAll(fileList);
                            return false;
                        }}
                    >
                        <Button size="small" type={missing.length ? "primary" : "default"} loading={uploading}>
                            <FontAwesomeIcon icon={faImages} className="mr-2" />
                            Tải ảnh cho trang
                        </Button>
                    </Upload>
                </div>
            </div>

            {images.length === 0 ? (
                <p className="text-xs text-content-3 m-0">
                    HTML chưa trỏ tới ảnh nào trong <code>/img/pages/</code>.
                </p>
            ) : (
                <div className="flex flex-col gap-1 max-h-40 overflow-auto">
                    {images.map(({ name }) => (
                        <div key={name} className="flex items-center gap-2 text-xs">
                            <Tag color={status[name] === false ? "red" : status[name] ? "green" : "default"}>
                                {status[name] === false ? "Thiếu" : status[name] ? "Đã có" : "…"}
                            </Tag>
                            <span className="truncate">{name}</span>
                        </div>
                    ))}
                </div>
            )}

            {missing.length > 0 && (
                <p className="text-xs text-amber-600 mt-2 mb-0">
                    Thiếu {missing.length} ảnh — bấm "Tải ảnh cho trang" rồi chọn đúng file{" "}
                    <code>{missing.map((i) => i.name).join(", ")}</code>. Tên file sẽ được đổi thành dạng{" "}
                    <code>{toPageImageName("Tên Ảnh Của Bạn.png")}</code>, đặt trùng tên là tự khớp.
                </p>
            )}
        </div>
    );
}

export default LandingPageImages;
